import  { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal } from "@/config/antd";
import { CloseOutlined } from "@/config/antd";
import Button from "@/components/common/Button";
import Input from "@/components/common/inputs/Input";
import Success from "@/components/common/alerts/Success";
import { useLoader } from "@/hooks/LoaderProvider";
import WebIcon from "@/components/common/Web_Icons";
import FileInput from "@/components/common/inputs/File_Input";
import { useDispatch,  } from "react-redux";
import {
  createMediaAsync,
  updateMediaAsync,
} from "../../../store/redux/slices/media/media_slice";
import { uploadFile } from "../../../store/redux/slices/user/file_upload_slice";
import Error from "../../../components/common/alerts/Error";
import { getUserId, getOrganizationId } from "../../../utils/token_functions";

const TabIndicator = ({ isActive }) => (
  <div
    className={`w-5 h-5 rounded-full border border-primary flex items-center justify-center
    ${isActive ? "bg-primary" : "bg-white"}`}>
    {isActive ? (
      <div className="w-3.5 h-3.5 rounded-full bg-white flex items-center justify-center">
        <div className="w-2 h-2 rounded-full bg-primary" />
      </div>
    ) : null}
  </div>
);

TabIndicator.propTypes = {
  isActive: PropTypes.bool,
};

const MediaDialog = ({ isVisible, onClose, onSuccess, mediaData }) => {
  const dispatch = useDispatch();
  const { showLoader, hideLoader } = useLoader();

  const isEditMode = !!mediaData;
  const [activeTab, setActiveTab] = useState("image");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedFiles, setSelectedFiles] = useState({
    image: [],
    video: null,
    document: null,
  });
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [videoUploadType, setVideoUploadType] = useState("manual");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Populate form data when editing
  useEffect(() => {
    if (mediaData && isVisible) {
      setTitle(mediaData.name || mediaData.title || "");
      setDescription(mediaData.description || "");

      // Set active tab based on available files
      if (mediaData.videoFiles?.length > 0 || mediaData.videoFiles?.[0]) {
        setActiveTab("video");
        setSelectedFiles((prev) => ({
          ...prev,
          video: {
            name: mediaData.videoFiles[0],
            url: mediaData.videoFiles[0],
            isExisting: true,
          },
        }));
      } else if (mediaData.documentFiles?.length > 0 || mediaData.documentFiles?.[0]) {
        setActiveTab("document");
        setSelectedFiles((prev) => ({
          ...prev,
          document: {
            name: mediaData.documentFiles[0],
            url: mediaData.documentFiles[0],
            isExisting: true,
          },
        }));
      } else {
        // Default to image if images exist or as a fallback
        setActiveTab("image");
        if (mediaData.imageFiles?.length > 0) {
          const existingImages = mediaData.imageFiles.map((imageUrl) => ({
            name: imageUrl,
            preview: imageUrl,
            url: imageUrl,
            isExisting: true,
          }));
          setSelectedFiles((prev) => ({
            ...prev,
            image: existingImages,
          }));
        }
      }
    } else {
      setTitle("");
      setDescription("");
      setSelectedFiles({
        image: [],
        video: null,
        document: null,
      });
      setActiveTab("image");
    }
  }, [mediaData, isVisible]);

  const handleAddImage = (e) => {
    const files = Array.from(e.target.files).map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      isExisting: false,
    }));

    setSelectedFiles((prev) => ({
      ...prev,
      image: [...prev.image, ...files],
    }));
    e.target.value = "";
  };

  const handleDeleteImage = (index) => {
    setSelectedFiles((prev) => ({
      ...prev,
      image: prev.image.filter((_, i) => i !== index),
    }));
  };

  const handleVideoFileChange = (file) => {
    setSelectedFiles((prev) => ({
      ...prev,
      video: { file, preview: URL.createObjectURL(file), isExisting: false },
    }));
  };

  const handleDeleteVideo = () => {
    setSelectedFiles((prev) => ({
      ...prev,
      video: null,
    }));
  };

  const handleDocumentFileChange = (file) => {
    setSelectedFiles((prev) => ({
      ...prev,
      document: {
        file,
        preview: URL.createObjectURL(file),
        isExisting: false,
      },
    }));
  };

  const handleDeleteDocument = () => {
    setSelectedFiles((prev) => ({
      ...prev,
      document: null,
    }));
    setFileInputKey(Date.now());
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    if (onSuccess) {
      onSuccess();
    }
    // Close the dialog
    onClose();
  };

  const handleUpdate = async () => {
    if (!title.trim()) {
      setErrorMessage("Please enter a file name");
      setShowErrorModal(true);
      return;
    }

    showLoader();
    onClose();

    try {
      const organizationID = getOrganizationId();
      const userID = getUserId();

      // Prepare file arrays
      let imageUrls = [];
      let videoUrl = null;
      let documentUrl = null;

      if (activeTab === "image") {
        const newImages = selectedFiles.image.filter((img) => !img.isExisting);
        const existingImages = selectedFiles.image
          .filter((img) => img.isExisting)
          .map((img) => img.url);

        if (newImages.length > 0) {
          const filesToUpload = newImages.map((img) => img.file);
          const uploadResult = await dispatch(
            uploadFile({ file: filesToUpload, folder: "media/images" })
          ).unwrap();

          // uploadFile returns { fileUrl: [ {url, filename}, ... ] } for arrays
          const newUrls = uploadResult.fileUrl.map((f) => f.url);
          imageUrls = [...existingImages, ...newUrls];
        } else {
          imageUrls = existingImages;
        }
      } else if (activeTab === "video") {
        if (selectedFiles.video) {
          if (selectedFiles.video.isExisting) {
            videoUrl = selectedFiles.video.url;
          } else if (selectedFiles.video.file) {
            const uploadResult = await dispatch(
              uploadFile({
                file: selectedFiles.video.file,
                folder: "media/videos",
              })
            ).unwrap();
            // uploadFile returns { fileUrl, filename } for single files
            videoUrl = uploadResult.fileUrl;
          } else if (selectedFiles.video.url) {
            videoUrl = selectedFiles.video.url;
          }
        }
      } else if (activeTab === "document") {
        if (selectedFiles.document) {
          if (selectedFiles.document.isExisting) {
            documentUrl = selectedFiles.document.url;
          } else if (selectedFiles.document.file) {
            const uploadResult = await dispatch(
              uploadFile({
                file: selectedFiles.document.file,
                folder: "media/documents",
              })
            ).unwrap();
            documentUrl = uploadResult.fileUrl;
          }
        }
      }

      const mediaPayload = {
        name: title,
        description: description || "",
        userID,
        organizationID,
        imageFiles: activeTab === "image" ? imageUrls : [],
        videoFiles: activeTab === "video" && videoUrl ? [videoUrl] : [],
        documentFiles:
          activeTab === "document" && documentUrl ? [documentUrl] : [],
      };

      if (isEditMode && mediaData?._id) {
        mediaPayload._id = mediaData._id;
        await dispatch(updateMediaAsync(mediaPayload)).unwrap();
      } else {
        await dispatch(createMediaAsync(mediaPayload)).unwrap();
      }

      setShowSuccessModal(true);
    } catch (error) {
      console.error("Media operation failed:", error);
      setErrorMessage(
        error.message || "An error occurred while processing your request"
      );
      setShowErrorModal(true);
    } finally {
      hideLoader();
    }
  };

  return (
    <>
      <Modal
        open={isVisible}
        onCancel={onClose}
        footer={null}
        centered
        width={600}
        closeIcon={null}
        styles={{
          body: { padding: 0 },
          content: {
            padding: 0,
            borderRadius: "16px",
            overflow: "hidden",
          },
          mask: { backgroundColor: "rgba(0, 0, 0, 0.75)" },
        }}>
        <div className="flex flex-col bg-white">
          <div className="p-4 flex justify-between items-center border-b border-[#E5E7EB]">
            <h2 className="heading-regular text-primary">
              {isEditMode ? "Edit Files" : "Add New File"}
            </h2>
            <CloseOutlined
              onClick={onClose}
              className="cursor-pointer bg-secondary p-1 rounded-full text-black"
            />
          </div>

          <div className="px-6 pt-4">
            <h1 className="heading-medium mb-4">File Upload</h1>
            <p className="text-small text-lighterGrey mb-4">
              Supported file format includes PNG, JPG, PDF, MP4, XT, Docs
            </p>

            <div className="flex gap-4 mb-6">
              <button
                className={`px-4 py-3 rounded-lg flex items-center gap-2.5
                  bg-white border border-primary text-small-xs whitespace-nowrap
                  ${activeTab === "image" ? "text-primary" : "text-gray-500"}`}
                onClick={() => setActiveTab("image")}>
                <TabIndicator isActive={activeTab === "image"} />
                Image Files
              </button>
              <button
                className={`px-4 py-3 rounded-lg flex items-center gap-2.5
                  bg-white border border-primary text-small-xs whitespace-nowrap
                  ${activeTab === "video" ? "text-primary" : "text-gray-500"}`}
                onClick={() => setActiveTab("video")}>
                <TabIndicator isActive={activeTab === "video"} />
                Video File
              </button>
              <button
                className={`px-4 py-3 rounded-lg flex items-center gap-2.5
                  bg-white border border-primary text-small-xs whitespace-nowrap
                  ${
                    activeTab === "document" ? "text-primary" : "text-gray-500"
                  }`}
                onClick={() => setActiveTab("document")}>
                <TabIndicator isActive={activeTab === "document"} />
                Document File
              </button>
            </div>

            {activeTab === "image" && (
              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-4 gap-4">
                  {selectedFiles.image.map((image, index) => (
                    <div key={index} className="relative aspect-square">
                      <img
                        src={image.preview}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <button
                          onClick={() => handleDeleteImage(index)}
                          className="p-2 rounded-full shadow-md hover:bg-gray-50">
                          <WebIcon
                            icon="Trash"
                            size="large"
                            className="text-primary"
                          />
                        </button>
                      </div>
                    </div>
                  ))}
                  <label className="aspect-square cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAddImage}
                      className="hidden"
                      multiple
                    />
                    <div className="w-full h-full bg-secondary rounded-lg flex items-center justify-center">
                      <WebIcon
                        icon="Trash"
                        size="large"
                        className="text-gray-400"
                      />
                    </div>
                  </label>
                </div>
              </div>
            )}

            {activeTab === "video" && (
              <>
                <div className="flex mb-6">
                  <button
                    className={`flex-1 py-3 px-6 rounded-lg text-center
                      ${
                        videoUploadType === "manual"
                          ? "bg-primary text-white"
                          : "bg-secondary text-primary"
                      }`}
                    onClick={() => setVideoUploadType("manual")}>
                    Manual Upload
                  </button>
                  <button
                    className={`flex-1 py-3 px-6 rounded-lg text-center
                      ${
                        videoUploadType === "url"
                          ? "bg-primary text-white"
                          : "bg-secondary text-primary"
                      }`}
                    onClick={() => setVideoUploadType("url")}>
                    Upload via URL
                  </button>
                </div>

                {videoUploadType === "manual" && (
                  <div className="mb-6">
                    <label className="w-full aspect-video cursor-pointer bg-gray-100 rounded-lg flex flex-col items-center justify-center hover:bg-gray-200 transition-colors relative">
                      <input
                        type="file"
                        accept="video/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            handleVideoFileChange(file);
                          }
                        }}
                        className="hidden"
                      />
                      {selectedFiles.video && !selectedFiles.video.isExisting ? (
                        <div className="w-full h-full flex flex-col items-center justify-center p-4">
                          {selectedFiles.video.preview ? (
                            <video
                              src={selectedFiles.video.preview}
                              controls
                              className="w-full h-full max-h-56 rounded-lg bg-black"
                            />
                          ) : (
                            <WebIcon
                              icon="VideoPlayer"
                              size="large"
                              className="text-primary mb-2"
                            />
                          )}
                          <p className="text-small text-gray-600 text-center truncate w-full mt-2">
                            {selectedFiles.video.file?.name || "Selected video"}
                          </p>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeleteVideo();
                            }}
                            className="mt-4 px-4 py-2 bg-white border border-danger text-danger rounded-lg text-xs hover:bg-danger hover:text-white transition-colors">
                            Delete Selected Video
                          </button>
                        </div>
                      ) : (
                        <>
                          <WebIcon
                            icon="AddImage"
                            size="large"
                            className="text-gray-400"
                          />
                          <p className="text-small text-gray-400 mt-2">Click to select video</p>
                        </>
                      )}
                    </label>
                  </div>
                )}

                {videoUploadType === "url" && (
                  <div className="mb-6">
                    <Input
                      label="VIDEO URL"
                      placeholder="Enter video URL"
                      value={selectedFiles.video?.url || ""}
                      onChange={(e) => {
                        setSelectedFiles((prev) => ({
                          ...prev,
                          video: { url: e.target.value, isExisting: false },
                        }));
                      }}
                    />
                    {selectedFiles.video?.url ? (
                      <div className="mt-4">
                        <video
                          src={selectedFiles.video.url}
                          controls
                          className="w-full max-h-64 rounded-lg bg-black"
                        />
                      </div>
                    ) : null}
                  </div>
                )}
              </>
            )}

            {activeTab === "document" && (
              <div className="space-y-4 mb-6">
                <FileInput
                  key={fileInputKey}
                  label="Upload Document"
                  placeholder="Choose a file"
                  onChange={(file) => {
                    if (file) {
                      handleDocumentFileChange(file);
                    }
                  }}
                  accept=".pdf,.doc,.docx"
                  id="document-upload"
                />
                {selectedFiles.document && (
                  <Button
                    text="Delete File"
                    color="secondary"
                    className="w-[136px] h-[36px] border border-danger whitespace-nowrap font-normal"
                    customBgColor="white"
                    customTextColor="danger"
                    iconPosition="left"
                    icon={<WebIcon icon="BlueTrashRed" size="large" />}
                    onClick={handleDeleteDocument}
                  />
                )}
              </div>
            )}

            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">File Details</h3>
              <div className="space-y-4">
                <Input
                  label="FILE NAME"
                  placeholder="Enter file name"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <Input
                  label="FILE DESCRIPTION"
                  placeholder="Enter file description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="w-full bg-secondary p-4 flex gap-3">
            <Button
              text="Cancel"
              color="secondary"
              className="flex-1 border border-primary"
              customBgColor="white"
              customTextColor="primary"
              onClick={onClose}
            />
            <Button
              text={isEditMode ? "Update" : "Upload"}
              color="primary"
              className="flex-1"
              customBgColor="primary"
              customTextColor="white"
              onClick={handleUpdate}
            />
          </div>
        </div>
      </Modal>

      <Success
        isVisible={showSuccessModal}
        onClose={handleSuccessClose}
        header={isEditMode ? "File Updated" : "File Uploaded"}
        paragraph={`You have successfully ${
          isEditMode ? "updated" : "uploaded"
        } this file.`}
        buttonText="Done"
      />

      <Error
        isVisible={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        header="Upload Failed!"
        paragraph={errorMessage}
        buttonText="Try Again"
        recoveryPath="/dashboard/media"
      />
    </>
  );
};

MediaDialog.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  mediaData: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    imageFiles: PropTypes.arrayOf(PropTypes.string),
    videoFiles: PropTypes.arrayOf(PropTypes.string),
    documentFiles: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default MediaDialog;
